/**
 * /admin — Configuração da Ferramenta de Orçamento (O1).
 *
 * 3 catálogos que parametrizam o cálculo do orçamento (spec
 * `docs/architecture/spec-ferramenta-orcamento.md`):
 *   1. Markups de material (lista escolhível por item, padrão 40%)
 *   2. Mão de obra + condições de pagamento (valores únicos)
 *   3. Taxas dos simuladores SICOOB / GRENKE por prazo
 *
 * Embutida em Catálogos (aba "Orçamento"). Gated por ORCAMENTO_ENABLED.
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Select,
  SelectOption,
  Switch,
  useToast,
  useConfirmDialog,
} from '@/components/ui';
import { errMsg } from '@/lib/api-error';
import { formatCurrency } from '@/lib/format';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import {
  useOrcamentoConfig,
  useOrcamentoConfigActions,
  type KitCatalogoItem,
  type MarkupMaterial,
  type ParcelamentoFaixa,
  type RegimeFaturamento,
  type SimuladorTaxa,
  type SimuladorTipo,
} from '@/lib/orcamento-config-store';
// 🔴 mig 593: o catálogo de divisões mora no store do ORÇAMENTO (é do item, não
// da config de preços) — uma lista só, servindo tela e Modelo.
import { useDivisoes, useDivisoesActions } from '@/lib/orcamentos-store';

/**
 * H3 (AJUDA-UX-QA): `secao` permite dividir a tela longa em duas abas na
 * Config do Comercial — 'precos' (markups, mão de obra, regime, faixas) e
 * 'kits-taxas' (kits e taxas dos simuladores). Sem `secao`, mostra tudo
 * (uso em /admin › Catálogos permanece igual).
 */
export function OrcamentoConfigPage({
  embedded = false,
  secao,
}: {
  embedded?: boolean;
  secao?: 'precos' | 'kits-taxas';
}) {
  const orcamentoOn = useMyFeatureFlag('ORCAMENTO_ENABLED');
  const { data, isLoading } = useOrcamentoConfig();
  const mostraPrecos = secao !== 'kits-taxas';
  const mostraKitsTaxas = secao !== 'precos';

  if (!orcamentoOn) {
    if (embedded) return null;
    return (
      <div className="min-w-0">
        <EmptyState
          title="Ferramenta de Orçamento indisponível"
          description="A Ferramenta de Orçamento ainda não está ativada para a sua conta."
        />
      </div>
    );
  }

  // Embutida (abas do Comercial / Catálogos) não repete padding nem h1 — quem
  // hospeda já tem os dois.
  return (
    <div className={embedded ? 'min-w-0' : 'min-w-0 max-w-3xl'}>
      {!embedded && (
        <PageHeader
          crumb="Configurações · Operação"
          title="Configuração do Orçamento"
          subtitle="Parâmetros que a Ferramenta de Orçamento usa pra calcular preço de venda, condições de pagamento e os simuladores."
        />
      )}
      <p className="text-sm text-ink-2 dark:text-ink-dark-2 mb-5">
        {embedded
          ? 'Parâmetros que a Ferramenta de Orçamento usa pra calcular preço de venda, condições de pagamento e os simuladores. Mudou aqui, vale para os próximos orçamentos.'
          : 'Mudou aqui, vale para os próximos orçamentos.'}
      </p>

      {isLoading || !data ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando configuração…</p>
      ) : (
        <div className="flex flex-col gap-6">
          {mostraPrecos && (
            <>
              <MarkupsSection markups={data.markups} />
              <MaoObraPagamentoSection
                markupMoPct={data.config.markupMoPct}
                condEntradaPct={data.config.condEntradaPct}
                condParceladoN={data.config.condParceladoN}
                cascataComissaoPct={data.config.cascataComissaoPct}
                cascataMargemSaudavelMin={data.config.cascataMargemSaudavelMin}
              />
              {/* R1 (RESUMO-REGIME): regime de faturamento da cascata. A antiga
                  "Régua de margem" (Baixo/Médio/Saudável por markup) MORREU —
                  a régua vigente é o limiar da margem líquida, acima. */}
              <RegimeFaturamentoSection
                regimePadrao={data.config.cascataRegimePadrao ?? 'direto'}
                permitirPorOrcamento={data.config.cascataPermitirRegimePorOrcamento ?? false}
              />
              <FaixasParcelamentoSection faixas={data.faixasParcelamento} />
              {/* 🔴 mig 593 — o SEGUNDO eixo do orçamento (divisão de custo).
                  Lista ÚNICA da empresa: a mesma alimenta o orçamento e o
                  Modelo. ❌ Nunca uma segunda lista. */}
              <DivisoesSection />
            </>
          )}
          {mostraKitsTaxas && (
            <>
              {/* M1 (AJUSTES3): markup padrão PRÓPRIO do kit insumo (mig 373). */}
              <KitMarkupPadraoSection valor={data.config.kitMarkupPadraoPct} />
              <KitCatalogoSection kits={data.kitCatalogo} />
              <TaxasSection taxas={data.taxas} />
            </>
          )}
          {/* A7: os Modelos de Orçamento saíram daqui — aba própria "Modelos de
              orçamento" em Configurações → Comercial — CRM. */}
        </div>
      )}
    </div>
  );
}

// ─── 1. Markups de material ─────────────────────────────────────────────

function MarkupsSection({ markups }: { markups: MarkupMaterial[] }) {
  const { createMarkup, updateMarkup, deleteMarkup, setDefaultMarkup } = useOrcamentoConfigActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [novo, setNovo] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');

  const actionError =
    errMsg(createMarkup.error) ??
    errMsg(updateMarkup.error) ??
    errMsg(deleteMarkup.error) ??
    errMsg(setDefaultMarkup.error);

  function adicionar() {
    const pct = Number(novo.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0) return;
    createMarkup.mutate({ percentual: pct }, { onSuccess: () => setNovo('') });
  }

  async function excluir(m: MarkupMaterial) {
    const ok = await confirm({
      title: 'Excluir markup?',
      message: `Excluir o markup de ${m.percentual}%? Itens que já usam esse percentual não mudam.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    deleteMarkup.mutate(m.id, {
      onSuccess: () => toast.show('Markup excluído'),
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir o markup.', { variant: 'error' }),
    });
  }

  function commitEdit(id: string) {
    const pct = Number(editVal.replace(',', '.'));
    if (Number.isFinite(pct) && pct >= 0) {
      updateMarkup.mutate({ id, patch: { percentual: pct } });
    }
    setEditId(null);
    setEditVal('');
  }

  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Markups de material
          <HelpTip texto="Markup é a porcentagem somada em cima do custo pra formar o preço de venda. Ex.: custo R$ 100 com markup 40% vira R$ 140." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          A lista de % que a equipe escolhe ao adicionar um item. O <strong>padrão</strong> já vem
          selecionado. Preço de venda = custo × (1 + markup).
        </p>
      </div>

      {actionError && (
        <Alert variant="danger" className="mb-3">
          {actionError}
        </Alert>
      )}

      <ul className="flex flex-col gap-1 mb-3">
        {markups.map((m) => (
          // FASE-C: a fileira quebra no celular (o grid de 3 colunas fixas
          // estourava os 375px com o switch + Excluir).
          <li
            key={m.id}
            data-testid={`orc-markup-${m.id}`}
            className="flex flex-wrap gap-x-2 gap-y-1.5 items-center px-2 py-1.5 rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface"
          >
            <div className="w-20 min-w-0 shrink-0">
              {editId === m.id ? (
                <Input
                  size="sm"
                  type="number"
                  autoFocus
                  value={editVal}
                  onChange={(e) => setEditVal(e.target.value)}
                  onBlur={() => commitEdit(m.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEdit(m.id);
                    } else if (e.key === 'Escape') {
                      setEditId(null);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(m.id);
                    setEditVal(String(m.percentual));
                  }}
                  className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 hover:text-red tabular-nums"
                  title="Clique pra editar"
                >
                  {m.percentual}%
                </button>
              )}
            </div>

            <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
              {m.isDefault ? (
                <Badge variant="success" size="sm">
                  Padrão
                </Badge>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDefaultMarkup.mutate(m.id)}
                  disabled={!m.ativo}
                  title={m.ativo ? 'Tornar padrão' : 'Ative antes de tornar padrão'}
                >
                  Tornar padrão
                </Button>
              )}
              {!m.ativo && (
                <Badge variant="neutral" size="sm">
                  Inativo
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 shrink-0">
              <Switch
                checked={m.ativo}
                onChange={() => updateMarkup.mutate({ id: m.id, patch: { ativo: !m.ativo } })}
                aria-label={`Ativar markup ${m.percentual}%`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                onClick={() => excluir(m)}
              >
                Excluir
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-32">
          <Label htmlFor="novo-markup">Novo % de markup</Label>
          <Input
            id="novo-markup"
            type="number"
            size="sm"
            value={novo}
            placeholder="Ex.: 38"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionar();
              }
            }}
          />
        </div>
        <Button size="sm" onClick={adicionar} disabled={!novo.trim()}>
          Adicionar
        </Button>
      </div>
    </Card>
  );
}

// ─── 1b. 🔴 mig 593 — Divisões de custo (o segundo eixo) ────────────────

/**
 * O catálogo das divisões de custo — as frentes de obra que o orçamentista
 * escolhe AO INSERIR cada item (CFTV · Cabeamento · Elétrica · Infra…).
 *
 * ⚠️ **Não é o "bloco"**: bloco é o kit padronizado que vem do Modelo, e um
 * mesmo kit costuma ter itens de mais de uma divisão (as câmeras são CFTV, os
 * cabos são infraestrutura). Os dois eixos convivem, e podem discordar.
 *
 * 🔴 **Divisão não se APAGA, se INATIVA**: item antigo continua apontando para
 * ela, e o indicador daquele orçamento tem de continuar dizendo a verdade.
 */
function DivisoesSection() {
  const { data, isLoading } = useDivisoes(true);
  const { criar, atualizar } = useDivisoesActions();
  const toast = useToast();
  const [novo, setNovo] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');

  const actionError = errMsg(criar.error) ?? errMsg(atualizar.error);
  const divisoes = data?.divisoes ?? [];

  function adicionar() {
    const nome = novo.trim();
    if (!nome) return;
    criar.mutate(nome, {
      onSuccess: () => {
        setNovo('');
        toast.show(`Divisão "${nome}" criada`);
      },
    });
  }

  function commitEdit(id: string) {
    const nome = editVal.trim();
    const antes = divisoes.find((d) => d.id === id)?.nome;
    if (nome && nome !== antes) atualizar.mutate({ id, patch: { nome } });
    setEditId(null);
    setEditVal('');
  }

  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Divisões de custo
          <HelpTip texto="As frentes de obra que aparecem no orçamento (CFTV, cabeamento, elétrica, infraestrutura…). O orçamentista escolhe uma ao adicionar cada item, e o orçamento passa a mostrar quanto custa cada frente. É diferente do bloco: o bloco é o kit que já vem montado pelo Modelo, e um mesmo kit costuma ter itens de mais de uma divisão. Esta lista vale para a empresa inteira — é ela que deixa um orçamento comparável com o outro." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          A lista que a equipe escolhe ao adicionar um item. Desligue a divisão que não usa mais —
          ela some do orçamento novo e continua valendo nos antigos.
        </p>
      </div>

      {actionError && (
        <Alert variant="danger" className="mb-3">
          {actionError}
        </Alert>
      )}

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando divisões…</p>
      ) : divisoes.length === 0 ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          Nenhuma divisão cadastrada. Enquanto a lista estiver vazia, o orçamento não pede divisão
          nenhuma.
        </p>
      ) : (
        <ul className="flex flex-col gap-1 mb-3">
          {divisoes.map((d) => (
            <li
              key={d.id}
              data-testid={`orc-divisao-${d.id}`}
              className="flex flex-wrap gap-x-2 gap-y-1.5 items-center px-2 py-1.5 rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface"
            >
              <div className="flex flex-1 min-w-[140px] items-center gap-2">
                {editId === d.id ? (
                  <Input
                    size="sm"
                    autoFocus
                    value={editVal}
                    onChange={(e) => setEditVal(e.target.value)}
                    onBlur={() => commitEdit(d.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitEdit(d.id);
                      } else if (e.key === 'Escape') {
                        setEditId(null);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditId(d.id);
                      setEditVal(d.nome);
                    }}
                    className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 hover:text-red text-left"
                    title="Clique pra renomear"
                  >
                    {d.nome}
                  </button>
                )}
                {!d.ativo && (
                  <Badge variant="neutral" size="sm">
                    Desligada
                  </Badge>
                )}
              </div>
              <Switch
                checked={d.ativo}
                onChange={() => atualizar.mutate({ id: d.id, patch: { ativo: !d.ativo } })}
                aria-label={`Usar a divisão ${d.nome} nos orçamentos novos`}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-48">
          <Label htmlFor="nova-divisao">Nova divisão</Label>
          <Input
            id="nova-divisao"
            size="sm"
            value={novo}
            placeholder="Ex.: Automação"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionar();
              }
            }}
          />
        </div>
        <Button
          size="sm"
          onClick={adicionar}
          disabled={!novo.trim() || criar.isPending}
          data-testid="orc-divisao-adicionar"
        >
          Adicionar
        </Button>
      </div>
    </Card>
  );
}

// ─── 2. Mão de obra + condições de pagamento ────────────────────────────

function MaoObraPagamentoSection(props: {
  markupMoPct: number;
  condEntradaPct: number;
  condParceladoN: number;
  cascataComissaoPct: number;
  cascataMargemSaudavelMin: number;
}) {
  const { updateConfig } = useOrcamentoConfigActions();
  const [mo, setMo] = useState(String(props.markupMoPct));
  const [entrada, setEntrada] = useState(String(props.condEntradaPct));
  const [parcelas, setParcelas] = useState(String(props.condParceladoN));
  // CA-4: comissão provisória + limiar da régua da margem líquida.
  const [comissao, setComissao] = useState(String(props.cascataComissaoPct));
  const [limiar, setLimiar] = useState(String(props.cascataMargemSaudavelMin));

  // Re-sincroniza se o snapshot mudar (após salvar / refetch).
  useEffect(() => {
    setMo(String(props.markupMoPct));
    setEntrada(String(props.condEntradaPct));
    setParcelas(String(props.condParceladoN));
    setComissao(String(props.cascataComissaoPct));
    setLimiar(String(props.cascataMargemSaudavelMin));
  }, [props.markupMoPct, props.condEntradaPct, props.condParceladoN, props.cascataComissaoPct, props.cascataMargemSaudavelMin]);

  const num = (s: string) => Number(s.replace(',', '.'));
  const dirty =
    num(mo) !== props.markupMoPct ||
    num(entrada) !== props.condEntradaPct ||
    Math.round(num(parcelas)) !== props.condParceladoN ||
    num(comissao) !== props.cascataComissaoPct ||
    num(limiar) !== props.cascataMargemSaudavelMin;

  function salvar() {
    updateConfig.mutate({
      markupMoPct: num(mo),
      condEntradaPct: num(entrada),
      condParceladoN: Math.max(1, Math.round(num(parcelas))),
      cascataComissaoPct: Math.max(0, num(comissao)),
      cascataMargemSaudavelMin: Math.max(0, num(limiar)),
    });
  }

  const error = errMsg(updateConfig.error);

  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Mão de obra e condições de pagamento
          <HelpTip texto="Aqui ficam o percentual aplicado sobre as diárias da equipe e as condições de pagamento padrão que aparecem em todo orçamento." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          O markup da mão de obra (venda = diária × dias × markup) e as três condições de pagamento
          aplicadas sobre o VALOR TOTAL.
        </p>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* FASE-C: 1 coluna no 375px — os rótulos longos ("Margem saudável a
          partir de") não cabiam em duas colunas. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="cfg-mo" className="mb-0">Markup mão de obra (%)</Label>
            <HelpTip texto="Porcentagem somada em cima do custo das diárias da equipe pra formar o preço da mão de obra no orçamento." />
          </span>
          <Input id="cfg-mo" type="number" size="sm" value={mo} onChange={(e) => setMo(e.target.value)} />
        </div>
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="cfg-comissao" className="mb-0">Comissão provisória (%)</Label>
            <HelpTip texto="Percentual de comissão sobre a venda total, usado na cascata de margem do orçamento. É provisório: o construtor de comissões vai substituir esta configuração." />
          </span>
          <Input
            id="cfg-comissao"
            type="number"
            size="sm"
            min={0}
            max={100}
            value={comissao}
            onChange={(e) => setComissao(e.target.value)}
            data-testid="cfg-cascata-comissao"
          />
        </div>
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="cfg-limiar" className="mb-0">Margem saudável a partir de (%)</Label>
            <HelpTip texto="A régua da margem LÍQUIDA do orçamento: abaixo deste valor ela aparece vermelha (baixa); a partir dele, verde (saudável)." />
          </span>
          <Input
            id="cfg-limiar"
            type="number"
            size="sm"
            min={0}
            max={90}
            value={limiar}
            onChange={(e) => setLimiar(e.target.value)}
            data-testid="cfg-cascata-limiar"
          />
        </div>
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="cfg-entrada" className="mb-0">Entrada (%)</Label>
            <HelpTip texto="Quanto o cliente paga no ato quando escolhe parcelar. O restante é dividido nas parcelas ao lado." />
          </span>
          <Input
            id="cfg-entrada"
            type="number"
            size="sm"
            value={entrada}
            onChange={(e) => setEntrada(e.target.value)}
          />
        </div>
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="cfg-parcelas" className="mb-0">Nº de parcelas</Label>
            <HelpTip texto="Em quantas vezes o valor que sobra depois da entrada é dividido na condição padrão." />
          </span>
          <Input
            id="cfg-parcelas"
            type="number"
            size="sm"
            value={parcelas}
            onChange={(e) => setParcelas(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-3">
        <Button size="sm" onClick={salvar} disabled={!dirty || updateConfig.isPending}>
          {updateConfig.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </Card>
  );
}

// ─── R1 (RESUMO-REGIME): regime de faturamento da cascata ───────────────

/**
 * Substitui a antiga "Régua de margem" (Baixo/Médio/Saudável por markup — o
 * modelo morreu na rodada RESUMO-REGIME). Duas configs: o regime PADRÃO que
 * todo orçamento usa e a trava que libera o seletor por orçamento no builder.
 */
function RegimeFaturamentoSection(props: {
  regimePadrao: RegimeFaturamento;
  permitirPorOrcamento: boolean;
}) {
  const { updateConfig } = useOrcamentoConfigActions();
  const toast = useToast();
  const error = errMsg(updateConfig.error);

  function salvar(patch: { cascataRegimePadrao?: RegimeFaturamento; cascataPermitirRegimePorOrcamento?: boolean }) {
    updateConfig.mutate(patch, {
      onSuccess: () => toast.show('Regime de faturamento salvo.'),
      onError: () => toast.show('Não foi possível salvar. Tente de novo.', { variant: 'error' }),
    });
  }

  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Regime de faturamento
          <HelpTip texto="Define quem emite a nota do material nos orçamentos e, com isso, como o imposto entra na conta da margem líquida. Vale pra todos os orçamentos — a chave abaixo permite exceções caso a caso." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          {/* PARTE C (08/09): o regime escolhe a ALÍQUOTA do material — a base é a
              mesma nos dois (ganho após a dedução do fornecedor). */}
          No <strong>Faturamento direto</strong> o fornecedor fatura o material pro cliente e a IGM
          emite NF de <strong>serviço</strong> sobre o seu ganho — vale a alíquota de serviço. No{' '}
          <strong>Faturamento IGM</strong> a IGM fatura e a NF é de <strong>produto</strong> — vale a
          alíquota de produto. A mão de obra é sempre serviço, e o imposto do fornecedor entra nas
          duas modalidades.
        </p>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="flex flex-col gap-3">
        <div className="w-56 max-w-full">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="cfg-regime-padrao" className="mb-0">Regime de faturamento padrão</Label>
            <HelpTip texto="O regime que TODO orçamento novo usa. Orçamentos já calculados guardam o regime da própria revisão — não mudam sozinhos quando você troca aqui." />
          </span>
          <Select
            id="cfg-regime-padrao"
            size="sm"
            data-testid="cfg-regime-padrao"
            value={props.regimePadrao}
            disabled={updateConfig.isPending}
            onChange={(e) => salvar({ cascataRegimePadrao: e.target.value as RegimeFaturamento })}
          >
            <SelectOption value="direto">Faturamento direto</SelectOption>
            <SelectOption value="revenda">Faturamento IGM</SelectOption>
          </Select>
        </div>

        <div className="flex items-start gap-2.5">
          <Switch
            checked={props.permitirPorOrcamento}
            disabled={updateConfig.isPending}
            onChange={() => salvar({ cascataPermitirRegimePorOrcamento: !props.permitirPorOrcamento })}
            aria-label="Permitir trocar o regime em cada orçamento"
            data-testid="cfg-regime-por-orcamento"
          />
          <span className="min-w-0 text-sm text-ink-1 dark:text-ink-dark-1 inline-flex flex-wrap items-center gap-1.5">
            Permitir trocar o regime em cada orçamento
            <HelpTip texto="Ligada, o construtor de orçamento ganha um seletor pra trocar o regime só daquele orçamento (que nasce herdando o padrão). Desligada, ninguém troca — todos os orçamentos usam o regime padrão, sem seletor na tela." />
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── O4: regras de parcelamento por faixa de valor ──────────────────────

function FaixasParcelamentoSection({ faixas }: { faixas: ParcelamentoFaixa[] }) {
  const { createFaixa, deleteFaixa } = useOrcamentoConfigActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [teto, setTeto] = useState('');
  const [entrada, setEntrada] = useState('30');
  const [parcelas, setParcelas] = useState('6');

  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Parcelamento por faixa de valor
          <HelpTip texto="Cada faixa diz: orçamentos até tal valor sugerem tal entrada e tal número de parcelas. O sistema escolhe a faixa sozinho conforme o total." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          O orçamento sugere entrada e parcelas conforme o valor total (ex.: até R$ 30.000 = entrada +
          3x; acima = entrada + 6x). O vendedor pode ajustar caso a caso no orçamento.
        </p>
      </div>
      {faixas.length > 0 && (
        <ul className="flex flex-col gap-1 mb-3">
          {faixas.map((f) => (
            <li
              key={f.id}
              className="flex items-start justify-between gap-2 px-3 py-1.5 rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface text-sm"
            >
              <span className="min-w-0 break-words text-ink-1 dark:text-ink-dark-1">
                {f.ateCentavos === null ? 'Acima das demais faixas' : `Até ${formatCurrency(f.ateCentavos / 100)}`} ·
                entrada {f.entradaPct}% + {f.parcelas}×{!f.ativo && ' · inativa'}
              </span>
              <button
                type="button"
                className="text-red hover:text-red-deep p-0.5 shrink-0"
                aria-label="Excluir faixa"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Excluir faixa?',
                    message: 'O orçamento volta a usar a próxima faixa (ou a condição padrão).',
                    confirmLabel: 'Excluir',
                    variant: 'destructive',
                  });
                  if (ok) deleteFaixa.mutate(f.id, { onError: () => toast.show('Não foi possível excluir.', { variant: 'error' }) });
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-44 max-w-full">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="fx-teto" className="mb-0">Até (R$ — vazio = sem teto)</Label>
            <HelpTip texto="Valor máximo do orçamento pra faixa valer. Deixe vazio pra criar a faixa que pega tudo acima das demais." />
          </span>
          <Input id="fx-teto" size="sm" type="number" value={teto} onChange={(e) => setTeto(e.target.value)} />
        </div>
        <div className="w-28">
          <Label htmlFor="fx-entrada">Entrada (%)</Label>
          <Input id="fx-entrada" size="sm" type="number" value={entrada} onChange={(e) => setEntrada(e.target.value)} />
        </div>
        <div className="w-24">
          <Label htmlFor="fx-parcelas">Parcelas</Label>
          <Input id="fx-parcelas" size="sm" type="number" min={1} value={parcelas} onChange={(e) => setParcelas(e.target.value)} />
        </div>
        <Button
          size="sm"
          onClick={() => {
            const t = teto.trim() === '' ? null : Math.round(Number(teto.replace(',', '.')) * 100);
            const e = Number(entrada.replace(',', '.'));
            const n = Math.max(1, Math.round(Number(parcelas)));
            if (t !== null && (!Number.isFinite(t) || t <= 0)) return;
            if (!Number.isFinite(e) || e < 0 || e > 100) return;
            createFaixa.mutate(
              { ateCentavos: t, entradaPct: e, parcelas: n, ordem: faixas.length + 1 },
              {
                onSuccess: () => {
                  setTeto('');
                  toast.show('Faixa adicionada.');
                },
                onError: () => toast.show('Não foi possível adicionar a faixa.', { variant: 'error' }),
              },
            );
          }}
        >
          Adicionar faixa
        </Button>
      </div>
    </Card>
  );
}

// ─── O7: catálogo de kits de insumos ────────────────────────────────────

/**
 * M1 (AJUSTES3, B4 aprovado): markup padrão PRÓPRIO do kit insumo — separado
 * do markup de material. Novos itens de kit usam ESTE valor; itens já
 * lançados não mudam (só via "Recalcular com o markup atual" no orçamento).
 */
function KitMarkupPadraoSection({ valor }: { valor: number | null }) {
  const { updateConfig } = useOrcamentoConfigActions();
  const toast = useToast();
  const [texto, setTexto] = useState(valor === null ? '' : String(valor));

  function salvar() {
    const n = Number(texto.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) {
      setTexto(valor === null ? '' : String(valor));
      return;
    }
    if (valor !== null && n === valor) return;
    updateConfig.mutate(
      { kitMarkupPadraoPct: n },
      {
        onSuccess: () => toast.show('Markup padrão do kit salvo.'),
        onError: () => {
          setTexto(valor === null ? '' : String(valor));
          toast.show('Não foi possível salvar. Tente de novo.', { variant: 'error' });
        },
      },
    );
  }

  return (
    <Card variant="flat" className="p-4">
      <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
        Markup padrão do kit insumo
        <HelpTip texto="O ganho aplicado sobre o custo do kit insumo (a reserva de imprevistos). É separado do markup de material. Vale pros itens de kit NOVOS — os já lançados só mudam pelo botão Recalcular, dentro do orçamento." />
      </h2>
      <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5 mb-3">
        Novos itens de kit entram no orçamento com este markup. Orçamentos já feitos não mudam
        sozinhos — lá existe o botão “Recalcular com o markup atual”, que mostra o antes e o
        depois em reais antes de aplicar.
      </p>
      <div className="w-40">
        <Label htmlFor="kit-markup-padrao">Markup do kit (%)</Label>
        <Input
          id="kit-markup-padrao"
          size="sm"
          type="number"
          min={0}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={salvar}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      </div>
      {valor === null && (
        <Alert variant="warning" className="mt-3">
          Sem valor configurado — os vendedores não conseguem adicionar itens de kit até você
          definir um markup aqui.
        </Alert>
      )}
    </Card>
  );
}

function KitCatalogoSection({ kits }: { kits: KitCatalogoItem[] }) {
  const { createKit, deleteKit } = useOrcamentoConfigActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [desc, setDesc] = useState('');
  const [valor, setValor] = useState('');
  const [markup, setMarkup] = useState('');

  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Catálogo de kits de insumos
          <HelpTip texto="Kit é um conjunto de materiais com custo fechado. O vendedor adiciona o kit inteiro no orçamento com um clique, sem digitar item por item." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          Kits prontos que o vendedor adiciona com um clique na carteira de imprevistos do orçamento.
          Markup vazio = usa o markup padrão do kit (campo acima).
        </p>
      </div>
      {kits.length > 0 && (
        <ul className="flex flex-col gap-1 mb-3">
          {kits.map((k) => (
            <li
              key={k.id}
              className="flex items-start justify-between gap-2 px-3 py-1.5 rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface text-sm"
            >
              <span className="min-w-0 break-words text-ink-1 dark:text-ink-dark-1">
                {k.descricao} ·{' '}
                {k.valorCentavos > 0
                  ? `custo sugerido ${formatCurrency(k.valorCentavos / 100)}`
                  : 'custo definido na hora'}
                {k.markupPct !== null ? ` · markup ${k.markupPct}%` : ''}
                {!k.ativo && ' · inativo'}
              </span>
              <button
                type="button"
                className="text-red hover:text-red-deep p-0.5 shrink-0"
                aria-label={`Excluir ${k.descricao}`}
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Excluir kit do catálogo?',
                    message: `Excluir "${k.descricao}"? Orçamentos que já usaram o kit não mudam.`,
                    confirmLabel: 'Excluir',
                    variant: 'destructive',
                  });
                  if (ok) deleteKit.mutate(k.id, { onError: () => toast.show('Não foi possível excluir.', { variant: 'error' }) });
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <Label htmlFor="kc-desc">Descrição</Label>
          <Input id="kc-desc" size="sm" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Ex.: Kit elétrico padrão" />
        </div>
        <div className="w-28">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="kc-valor" className="mb-0">Custo (R$)</Label>
            {/* URGENTE 05/08 (decisão do Matheus): custo é SUGESTÃO — vazio
                vale; o orçamentista digita o custo real ao montar o orçamento
                (o catálogo pré-preenche, nunca grava direto). */}
            <HelpTip texto="Opcional — é só uma sugestão. Ao usar o kit num orçamento, o custo vem pré-preenchido e o orçamentista confirma ou muda antes de gravar. Vazio = ele digita na hora." />
          </span>
          <Input id="kc-valor" size="sm" type="number" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="na hora" />
        </div>
        <div className="w-28">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="kc-markup" className="mb-0">Markup (%)</Label>
            <HelpTip texto="Porcentagem em cima do custo deste kit. Deixe vazio pra usar o markup padrão de material." />
          </span>
          <Input id="kc-markup" size="sm" type="number" value={markup} onChange={(e) => setMarkup(e.target.value)} placeholder="padrão" />
        </div>
        <Button
          size="sm"
          disabled={!desc.trim()}
          onClick={() => {
            const v = valor.trim() === '' ? 0 : Math.round(Number(valor.replace(',', '.')) * 100);
            if (!Number.isFinite(v) || v < 0) return;
            const m = markup.trim() === '' ? null : Number(markup.replace(',', '.'));
            createKit.mutate(
              { descricao: desc.trim(), valorCentavos: v, markupPct: m },
              {
                onSuccess: () => {
                  setDesc('');
                  setValor('');
                  setMarkup('');
                  toast.show('Kit adicionado ao catálogo.');
                },
                onError: () => toast.show('Não foi possível adicionar o kit.', { variant: 'error' }),
              },
            );
          }}
        >
          Adicionar kit
        </Button>
      </div>
    </Card>
  );
}

// ─── 3. Taxas dos simuladores ───────────────────────────────────────────

function TaxasSection({ taxas }: { taxas: SimuladorTaxa[] }) {
  return (
    <Card variant="flat" className="p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
          Taxas dos simuladores
          <HelpTip texto="Alimentam os simuladores mostrados junto do orçamento: SICOOB simula financiamento (juros ao mês) e GRENKE simula locação do equipamento (fator mensal)." />
        </h2>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">
          Taxas usadas nos simuladores de financiamento. <strong>SICOOB</strong>: taxa mensal por
          prazo. <strong>GRENKE</strong>: fator mensal de locação por prazo.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <TaxaTipoTable
          tipo="sicoob"
          titulo="Financiamento SICOOB"
          unidade="% ao mês"
          taxas={taxas.filter((t) => t.tipo === 'sicoob')}
        />
        <TaxaTipoTable
          tipo="grenke"
          titulo="Locação GRENKE"
          unidade="% ao mês (fator)"
          taxas={taxas.filter((t) => t.tipo === 'grenke')}
        />
      </div>
    </Card>
  );
}

function TaxaTipoTable({
  tipo,
  titulo,
  unidade,
  taxas,
}: {
  tipo: SimuladorTipo;
  titulo: string;
  unidade: string;
  taxas: SimuladorTaxa[];
}) {
  const { createTaxa, updateTaxa, deleteTaxa } = useOrcamentoConfigActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [novoPrazo, setNovoPrazo] = useState('');
  const [novoValor, setNovoValor] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');

  const error = errMsg(createTaxa.error) ?? errMsg(updateTaxa.error) ?? errMsg(deleteTaxa.error);

  async function excluir(t: SimuladorTaxa) {
    const ok = await confirm({
      title: 'Excluir taxa?',
      message: `Excluir a taxa de ${t.prazo} meses (${titulo})?`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    deleteTaxa.mutate(t.id, {
      onSuccess: () => toast.show('Taxa excluída'),
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir a taxa.', { variant: 'error' }),
    });
  }

  function adicionar() {
    const prazo = Math.round(Number(novoPrazo));
    const valor = Number(novoValor.replace(',', '.'));
    if (!Number.isFinite(prazo) || prazo < 1 || !Number.isFinite(valor) || valor < 0) return;
    createTaxa.mutate(
      { tipo, prazo, valor },
      {
        onSuccess: () => {
          setNovoPrazo('');
          setNovoValor('');
        },
      },
    );
  }

  function commitEdit(id: string) {
    const valor = Number(editVal.replace(',', '.'));
    if (Number.isFinite(valor) && valor >= 0) {
      updateTaxa.mutate({ id, patch: { valor } });
    }
    setEditId(null);
    setEditVal('');
  }

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 mb-1">
        <h3 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">{titulo}</h3>
        <span className="text-xs text-ink-3 dark:text-ink-dark-3">{unidade}</span>
      </div>

      {error && (
        <Alert variant="danger" className="mb-2">
          {error}
        </Alert>
      )}

      <ul className="flex flex-col gap-1 mb-2">
        {taxas.map((t) => (
          <li
            key={t.id}
            data-testid={`orc-taxa-${t.id}`}
            className="flex flex-wrap gap-x-2 gap-y-1.5 items-center px-2 py-1.5 rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface"
          >
            <span className="w-14 shrink-0 text-sm text-ink-2 dark:text-ink-dark-2 tabular-nums">{t.prazo}m</span>
            <div className="flex-1 min-w-0">
              {editId === t.id ? (
                <Input
                  size="sm"
                  type="number"
                  autoFocus
                  value={editVal}
                  onChange={(e) => setEditVal(e.target.value)}
                  onBlur={() => commitEdit(t.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEdit(t.id);
                    } else if (e.key === 'Escape') {
                      setEditId(null);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(t.id);
                    setEditVal(String(t.valor));
                  }}
                  className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 hover:text-red tabular-nums"
                  title="Clique pra editar"
                >
                  {t.valor}
                </button>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 shrink-0">
              <Switch
                checked={t.ativo}
                onChange={() => updateTaxa.mutate({ id: t.id, patch: { ativo: !t.ativo } })}
                aria-label={`Ativar taxa ${t.prazo}m`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                onClick={() => excluir(t)}
              >
                Excluir
              </Button>
            </div>
          </li>
        ))}
        {taxas.length === 0 && (
          <li className="text-sm text-ink-3 dark:text-ink-dark-3 px-2 py-1.5">Nenhuma taxa.</li>
        )}
      </ul>

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-20">
          <Label htmlFor={`novo-prazo-${tipo}`}>Prazo (m)</Label>
          <Input
            id={`novo-prazo-${tipo}`}
            type="number"
            size="sm"
            value={novoPrazo}
            placeholder="36"
            onChange={(e) => setNovoPrazo(e.target.value)}
          />
        </div>
        <div className="w-24">
          <Label htmlFor={`novo-valor-${tipo}`}>Taxa</Label>
          <Input
            id={`novo-valor-${tipo}`}
            type="number"
            size="sm"
            value={novoValor}
            placeholder="2.5"
            onChange={(e) => setNovoValor(e.target.value)}
          />
        </div>
        <Button size="sm" onClick={adicionar} disabled={!novoPrazo.trim() || !novoValor.trim()}>
          Adicionar
        </Button>
      </div>
    </div>
  );
}
